/**
 * NAMED DRAFTS + ONE PREVIEW LINK (2026-09-18, RMT pilot feedback call, item 1).
 *
 * Laura built a blast and could not show it to Devri for a second set of eyes —
 * screenshots were the workaround. Jack's ruling, after two mockup rounds
 * (`_mockups/DRAFTS-AND-PREVIEW-LINK-20260918.html`):
 *
 *   · sharing lives ONLY in Design Email, under "… More" — no reviewer accounts,
 *     no in-app comments for now. The reviewer gets ONE link anyone can open, the
 *     same hosted-preview shape the Airtable request row gets ("Studio Preview
 *     URL"), and it is LIVE: the same URL shows the draft as last saved.
 *   · "Save as draft" sits beside ★ Save favorite. Drafts get their own mode in
 *     the template picker, in the SAME RMT → division folders as Favorites, with
 *     delete on both.
 *   · a draft clears itself once the eblast is sent.
 *
 * What a named draft IS: the same shape the browser draft (`services/draft.ts`)
 * already keeps — form, audience, wizard page, Studio blocks — plus a name and
 * the rendered HTML, stored server-side under a stable id (division + name), so
 * re-saving the same name overwrites in place and the preview link never moves.
 *
 * Was DEV / QA ONLY (Jack, 09-18: "flag it should only be in dev/qa"). ON EVERYWHERE
 * from 2026-09-23 (Jack: "drafts and preview should be in prod") — see
 * `namedDraftsEnabled` below for the runtime off switch.
 */
import type { Draft } from "./draft";
import { NONPROD_HOST } from "./api";

/** The payload a named draft carries — the browser draft minus its own header. */
export type NamedDraftDoc = Omit<Draft, "v" | "savedAt" | "who">;

export interface NamedDraft {
  /** stable id — `<DIV>-<slug of name>`; re-saving the same name overwrites */
  id: string;
  div: string;
  name: string;
  who: string;
  /** ISO time of the last save */
  savedAt: string;
  /** the public hosted preview — same URL every save */
  previewUrl: string;
  /** the template the build was assembled from, "" for a bespoke document */
  tpl?: string;
  /** set when a request was submitted from this draft — cleared once that eblast is sent */
  submittedAt?: string;
  recordId?: string;
}

/**
 * Non-prod host test. Matches how `PROXY_MODE` decides "non-prod": the -dev/-qa/-uat
 * hosts, local dev servers, and a double-clicked file:// build. Still the gate for the
 * overlay editor (services/skyExtend.ts), which stays out of production.
 */
export function nonProdHost(loc?: { hostname: string; protocol: string }): boolean {
  try {
    const l = loc || (typeof window !== "undefined" ? window.location : undefined);
    if (!l) return false;
    if (l.protocol === "file:") return true;
    const h = l.hostname;
    return NONPROD_HOST.test(h) || h === "localhost" || h === "127.0.0.1";
  } catch {
    return false;
  }
}

/**
 * ── DRAFTS + THE PREVIEW LINK ARE ON IN PRODUCTION (2026-09-23, Jack) ────────────────
 * "drafts and preview should be in prod." Every host now, production included. The
 * runtime switch stays as the way back without a build: `features.namedDrafts` in
 * config.js (FEATURE_NAMED_DRAFTS=false on the pod) turns the menu items and the
 * picker mode off for that environment; true forces them on.
 */
export function namedDraftsEnabled(_loc?: { hostname: string; protocol: string }): boolean {
  const o = namedDraftsFlag();
  return typeof o === "boolean" ? o : true;
}
function namedDraftsFlag(): boolean | undefined {
  const cfg = typeof window !== "undefined" ? window.__APP_CONFIG__ : undefined;
  const v = cfg && cfg.features ? cfg.features.namedDrafts : undefined;
  return typeof v === "boolean" ? v : undefined;
}

/** `JAX` + `Coming Soon · Edenbrooke` → `JAX-coming-soon-edenbrooke` (mirrors the server) */
export function namedDraftId(div: string, name: string): string {
  const d = String(div || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  const s = String(name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);
  return d && s ? d + "-" + s : "";
}

/** "saved just now" · "saved 12 min ago" · "saved Sep 11" — same voice as the browser draft */
export function namedDraftAge(savedAt: string, now: number = Date.now()): string {
  const t = Date.parse(savedAt);
  if (!t) return "";
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return m + " min ago";
  const h = Math.round(m / 60);
  if (h < 24) return h + (h === 1 ? " hour ago" : " hours ago");
  const d = Math.round(h / 24);
  return d === 1 ? "yesterday" : d + " days ago";
}
